import { type ChangeEvent, type ReactNode, useId } from 'react';

import { type ConfigFieldProps } from './types';
import { useSelectedNodeField } from './useSelectedNodeField';
import { useReactFlowViewContext } from '../../context/context';

export interface BaseToggleFieldProps<
  TActiveState = boolean,
  TInactiveState = boolean,
> extends ConfigFieldProps {
  label: ReactNode;
  helperText?: ReactNode;
  onChange?: (value: TActiveState | TInactiveState) => void;
  /**
   * The state to display when the field has no value yet.
   * Set this when the backend treats an unset field as active, so the toggle
   * does not render as off while the flow actually behaves as on.
   * @default the inactive state
   */
  defaultState?: TActiveState | TInactiveState;
}

interface ToggleStates<TActiveState = boolean, TInactiveState = boolean> {
  activeState: TActiveState;
  inactiveState: TInactiveState;
}

export type UseToggleFieldProps<
  TActiveState = boolean,
  TInactiveState = boolean,
> = BaseToggleFieldProps<TActiveState | TInactiveState> &
  (TActiveState extends boolean
    ? { toggleStates?: ToggleStates<TActiveState, TInactiveState> }
    : { toggleStates: ToggleStates<TActiveState, TInactiveState> });

export function useToggleField<TActiveState = boolean, TInactiveState = boolean>(
  props: UseToggleFieldProps<TActiveState, TInactiveState>
) {
  const {
    fieldName,
    label,
    helperText,
    onChange,
    defaultState,
    toggleStates = {
      activeState: true as TActiveState,
      inactiveState: false as TInactiveState,
    },
  } = props;
  const { field, visibleError, isFieldErrorVisible, setValue } = useSelectedNodeField<
    TActiveState | TInactiveState
  >(fieldName);
  const { isEditable } = useReactFlowViewContext();
  const fieldId = useId();
  const displayedHelperText = visibleError ?? helperText;
  const controlProps = {
    id: fieldId,
    label,
    checked: (field.value ?? defaultState) === toggleStates.activeState,
    disabled: !isEditable,
    onChange: (_event: ChangeEvent, isChecked: boolean) => {
      const { activeState, inactiveState } = toggleStates;
      const value = isChecked ? activeState : inactiveState;
      setValue(value);
      onChange?.(value);
    },
  };
  return {
    controlProps,
    label,
    displayedHelperText,
    isFieldErrorVisible,
  };
}
